import { useId } from 'react';

const VIEW_W = 1200;
const VIEW_H = 120;
const CY = 60;
const AMP = 22;
const STRAND_SEGMENTS = 96;
const STRAND_STROKE_WIDTH = 5.75;

function buildStrandPaths() {
  let dTop = '';
  let dBot = '';
  for (let i = 0; i <= STRAND_SEGMENTS; i++) {
    const x = (i / STRAND_SEGMENTS) * VIEW_W;
    const t = (2 * Math.PI * x) / VIEW_W;
    const yTop = CY + AMP * Math.sin(t);
    const yBot = CY - AMP * Math.sin(t);
    const cmd = i === 0 ? 'M' : 'L';
    dTop += `${cmd}${x.toFixed(2)},${yTop.toFixed(2)}`;
    dBot += `${cmd}${x.toFixed(2)},${yBot.toFixed(2)}`;
  }
  return { dTop, dBot };
}

function buildBasePairs() {
  const lines: { x: number; y1: number; y2: number }[] = [];
  const step = 75;
  for (let x = step / 2; x < VIEW_W; x += step) {
    const t = (2 * Math.PI * x) / VIEW_W;
    lines.push({
      x,
      y1: CY + AMP * Math.sin(t),
      y2: CY - AMP * Math.sin(t),
    });
  }
  return lines;
}

const STRANDS = buildStrandPaths();
const PAIRS = buildBasePairs();

export type DnaHelixFade = 'left' | 'right' | 'none';

type Props = {
  fade?: DnaHelixFade;
  className?: string;
  repeat?: number;
};

function HelixTile() {
  return (
    <>
      <path
        fill="none"
        stroke="var(--dna-magenta, rgb(209, 0, 145))"
        strokeWidth={STRAND_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        d={STRANDS.dTop}
      />
      <path
        fill="none"
        stroke="var(--dna-navy, rgb(13, 23, 96))"
        strokeWidth={STRAND_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        d={STRANDS.dBot}
      />
    </>
  );
}

export function DnaHelixStrip({ fade = 'none', className, repeat = 1 }: Props) {
  const uid = useId().replace(/:/g, '');
  const maskId = `dna-mask-${uid}`;
  const gradId = `dna-fade-${uid}`;
  const totalW = VIEW_W * Math.max(1, repeat);

  const fadeGradient =
    fade === 'left' ? (
      <linearGradient id={gradId} gradientUnits="objectBoundingBox" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="white" />
        <stop offset="100%" stopColor="black" />
      </linearGradient>
    ) : fade === 'right' ? (
      <linearGradient id={gradId} gradientUnits="objectBoundingBox" x1="1" y1="0" x2="0" y2="0">
        <stop offset="0%" stopColor="white" />
        <stop offset="100%" stopColor="black" />
      </linearGradient>
    ) : null;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${totalW} ${VIEW_H}`}
      width="100%"
      height={VIEW_H}
      preserveAspectRatio="none"
      role="img"
      aria-label="DNA double helix"
    >
      <defs>
        {fadeGradient}
        {fade !== 'none' && (
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width={totalW} height={VIEW_H}>
            <rect x="0" y="0" width={totalW} height={VIEW_H} fill={`url(#${gradId})`} />
          </mask>
        )}
      </defs>
      <g mask={fade !== 'none' ? `url(#${maskId})` : undefined}>
        {Array.from({ length: Math.max(1, repeat) }, (_, k) => (
          <g key={k} transform={`translate(${k * VIEW_W},0)`}>
            <HelixTile />
          </g>
        ))}
      </g>
    </svg>
  );
}
